import React from 'react';

import {chipStyle} from '../lib/formStyles.js';

// ============================================================================
// Toolbar — the one list toolbar (Design Constitution L01–L03, L07, Q07, Q01)
// ----------------------------------------------------------------------------
// Search on the left; labeled secondary buttons ('Filters (2)', 'Sort',
// 'Views', 'Columns', 'Export') that open their panel below the bar; the one
// green primary far right. No trailing '⋯' on a page toolbar (Q12); a row's
// own actions use OverflowMenu. A control whose panel is
// open shows the green outline (Q01). The open panel renders under the bar
// (children); active filters show as removable gray chips with 'Clear All'
// (FilterChips, L03).
// Only lists that show a toolbar today get the full one (Q07); /pig/sows
// keeps just its search.
//
//   search:   <SearchInput …/> (L02)
//   controls: [{ key, label, count?, open?, onClick }]
//   primary:  the page's green action, e.g. <Button variant="primary">
//   children: the open panel, if any
// ============================================================================

export type ToolbarControl = {
  key: string;
  label: string;
  count?: number;
  open?: boolean;
  onClick: () => void;
};

type ToolbarProps = {
  search?: React.ReactNode;
  controls?: ToolbarControl[];
  primary?: React.ReactNode;
  label?: string;
  children?: React.ReactNode;
};

export function controlLabel(c: Pick<ToolbarControl, 'label' | 'count'>): string {
  return c.count && c.count > 0 ? `${c.label} (${c.count})` : c.label;
}

export default function Toolbar({search, controls = [], primary, label = 'List tools', children}: ToolbarProps) {
  return (
    <div data-toolbar="1" style={{marginBottom: 14}}>
      <div role="toolbar" aria-label={label} style={{display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8}}>
        {search && <div style={{flex: '1 1 240px', minWidth: 200, maxWidth: 420}}>{search}</div>}
        {controls.map((c) => (
          <button
            key={c.key}
            type="button"
            className={c.open ? 'wcf-clickable wcf-selected' : 'wcf-clickable'}
            aria-expanded={c.open ?? false}
            data-toolbar-control={c.key}
            onClick={c.onClick}
            style={chipStyle(Boolean(c.open))}
          >
            {controlLabel(c)}
          </button>
        ))}
        {primary && <div style={{marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8}}>{primary}</div>}
      </div>
      {children && (
        <div
          data-toolbar-panel="1"
          style={{
            marginTop: 10,
            padding: 14,
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 12,
          }}
        >
          {children}
        </div>
      )}
    </div>
  );
}

// ── Search (L02): magnifier, inline ×, results as you type. The caller keeps
// the value; usePersistentViewState(key, '') remembers it for the session.
type SearchInputProps = {
  value: string;
  onChange: (next: string) => void;
  label?: string;
  placeholder?: string;
};

export function SearchInput({value, onChange, label = 'Search', placeholder}: SearchInputProps) {
  const ref = React.useRef<HTMLInputElement | null>(null);
  return (
    <div data-search-input="1" style={{position: 'relative'}}>
      <span
        aria-hidden="true"
        style={{
          position: 'absolute',
          left: 12,
          top: '50%',
          transform: 'translateY(-50%)',
          color: 'var(--label)',
          fontSize: 15,
          pointerEvents: 'none',
        }}
      >
        ⌕
      </span>
      <input
        ref={ref}
        type="search"
        aria-label={label}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape' && value) {
            // Clearing consumes Esc: it must not also close an enclosing modal.
            e.preventDefault();
            e.stopPropagation();
            onChange('');
          }
        }}
        style={{paddingLeft: 34, paddingRight: value ? 44 : 12, minHeight: 44}}
      />
      {value && (
        <button
          type="button"
          className="wcf-clickable"
          aria-label="Clear search"
          data-search-clear="1"
          onClick={() => {
            onChange('');
            ref.current?.focus();
          }}
          style={{
            position: 'absolute',
            right: 0,
            top: 0,
            width: 44,
            height: 44,
            border: 'none',
            background: 'transparent',
            color: 'var(--text-2)',
            fontFamily: 'inherit',
            fontSize: 18,
            cursor: 'pointer',
          }}
        >
          ×
        </button>
      )}
    </div>
  );
}

// ── Active filters (L03): removable gray chips plus 'Clear All'.
export type ActiveFilter = {key: string; label: React.ReactNode; onRemove: () => void};

export function FilterChips({filters, onClearAll}: {filters: ActiveFilter[]; onClearAll: () => void}) {
  if (filters.length === 0) return null;
  return (
    <div
      data-filter-chips="1"
      style={{display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginBottom: 12}}
    >
      {filters.map((f) => (
        <span
          key={f.key}
          data-filter-chip={f.key}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 2,
            paddingLeft: 12,
            borderRadius: 999,
            background: 'var(--surface-2)',
            color: 'var(--ink)',
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          {f.label}
          <button
            type="button"
            className="wcf-clickable"
            aria-label="Remove filter"
            onClick={f.onRemove}
            style={{
              width: 44,
              height: 44,
              border: 'none',
              background: 'transparent',
              color: 'var(--text-2)',
              fontFamily: 'inherit',
              fontSize: 16,
              cursor: 'pointer',
            }}
          >
            ×
          </button>
        </span>
      ))}
      <button
        type="button"
        className="wcf-clickable"
        data-filter-clear-all="1"
        onClick={onClearAll}
        style={{
          minHeight: 44,
          padding: '0 8px',
          border: 'none',
          background: 'transparent',
          color: 'var(--brand)',
          fontFamily: 'inherit',
          fontSize: 13,
          fontWeight: 700,
          cursor: 'pointer',
        }}
      >
        Clear All
      </button>
    </div>
  );
}
